import styled, { keyframes, css } from "styled-components";

/* ======================== ANIMATIONS ======================== */
const fadeIn = keyframes`
   from { opacity: 0; transform: translateY(20px); }
   to { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
   0% { background-position: -200% center; }
   100% { background-position: 200% center; }
`;

const pulse = keyframes`
   0%, 100% { opacity: 0.4; }
   50% { opacity: 1; }
`;

const float = keyframes`
   0%, 100% { transform: translateY(0px); }
   50% { transform: translateY(-8px); }
`;

const orbitSlow = keyframes`
   from { transform: rotate(0deg); }
   to { transform: rotate(360deg); }
`;

const slideInRight = keyframes`
   from { opacity: 0; transform: translateX(40px); }
   to { opacity: 1; transform: translateX(0); }
`;

const scaleIn = keyframes`
   from { opacity: 0; transform: scale(0.9); }
   to { opacity: 1; transform: scale(1); }
`;

const progressFill = keyframes`
   from { width: 0; }
`;

const orbGlow = keyframes`
   0%, 100% { opacity: 0.2; }
   50% { opacity: 0.58; }
`;

/* ======================== AGENT ORB ======================== */
export const AgentOrbWrap = styled.div`
   display: flex;
   align-items: center;
   justify-content: center;

   svg {
      animation: ${float} 4s ease-in-out infinite;
      filter: drop-shadow(0 0 10px rgba(255, 255, 255, 0.2));
   }

   .orb-halo {
      animation: ${orbGlow} 3s ease-in-out infinite;
   }

   .orb-node {
      animation: ${orbGlow} 2.5s ease-in-out infinite;
   }
`;

/* ======================== MAIN LAYOUT ======================== */
export const PageContainer = styled.div`
   display: flex;
   flex-direction: column;
   height: 100vh;
   background: #f8fafc;
   overflow: hidden;
   position: relative;

   [data-theme="dark"] & {
      background: #0c1222;
   }
`;

export const TopBar = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   padding: 0 32px;
   height: 60px;
   background: #fff;
   border-bottom: 1px solid #e2e8f0;
   flex-shrink: 0;
   z-index: 100;

   [data-theme="dark"] & {
      background: #111827;
      border-bottom-color: #1f2937;
   }
`;

export const TopBarLeft = styled.div`
   display: flex;
   align-items: center;
   gap: 14px;
`;

export const BackButton = styled.button`
   display: flex;
   align-items: center;
   gap: 6px;
   padding: 6px 14px;
   border: 1px solid #e2e8f0;
   border-radius: 8px;
   background: transparent;
   color: #64748b;
   font-size: 13px;
   cursor: pointer;
   transition: all 0.2s;

   &:hover {
      background: #f1f5f9;
      color: #334155;
      border-color: #cbd5e1;
   }

   [data-theme="dark"] & {
      border-color: #374151;
      color: #9ca3af;
      &:hover {
         background: #1f2937;
         color: #d1d5db;
      }
   }
`;

export const LogoMark = styled.div`
   display: flex;
   align-items: center;
   gap: 10px;

   .logo-icon {
      width: 34px;
      height: 34px;
      background: linear-gradient(135deg, #06b6d4, #8b5cf6);
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;
      font-size: 16px;
      box-shadow: 0 2px 8px rgba(6, 182, 212, 0.3);
   }

   .logo-text {
      font-size: 16px;
      font-weight: 700;
      color: #1e293b;
      letter-spacing: -0.3px;

      span {
         background: linear-gradient(135deg, #06b6d4, #8b5cf6);
         -webkit-background-clip: text;
         -webkit-text-fill-color: transparent;
      }

      [data-theme="dark"] & {
         color: #f1f5f9;
      }
   }
`;

export const TopBarRight = styled.div`
   display: flex;
   align-items: center;
   gap: 10px;
`;

export const TopBarBtn = styled.button<{ $primary?: boolean }>`
   display: flex;
   align-items: center;
   gap: 6px;
   padding: 8px 18px;
   border-radius: 8px;
   font-size: 13px;
   font-weight: 600;
   cursor: pointer;
   transition: all 0.2s;
   border: none;

   ${p => p.$primary ? css`
      background: linear-gradient(135deg, #06b6d4, #0891b2);
      color: #fff;
      box-shadow: 0 2px 8px rgba(6,182,212,0.3);
      &:hover { box-shadow: 0 4px 16px rgba(6,182,212,0.4); transform: translateY(-1px); }
   ` : css`
      background: #f1f5f9;
      color: #475569;
      border: 1px solid #e2e8f0;
      &:hover { background: #e2e8f0; }
      [data-theme="dark"] & {
         background: #1f2937;
         color: #d1d5db;
         border-color: #374151;
         &:hover { background: #374151; }
      }
   `}
`;

/* ======================== BODY LAYOUT ======================== */
export const BodyLayout = styled.div`
   display: flex;
   flex: 1;
   overflow: hidden;
`;

export const StepsSidebar = styled.div`
   width: 260px;
   background: #fff;
   border-right: 1px solid #e2e8f0;
   padding: 24px 0;
   overflow-y: auto;
   flex-shrink: 0;

   [data-theme="dark"] & {
      background: #111827;
      border-right-color: #1f2937;
   }
`;

export const SidebarTitle = styled.div`
   padding: 0 20px 16px;
   font-size: 11px;
   font-weight: 700;
   text-transform: uppercase;
   letter-spacing: 1.2px;
   color: #94a3b8;
`;

export const StepItem = styled.div<{ $active?: boolean; $completed?: boolean }>`
   display: flex;
   align-items: center;
   gap: 12px;
   padding: 12px 20px;
   cursor: pointer;
   transition: all 0.2s;
   position: relative;

   ${p => p.$active && css`
      background: linear-gradient(90deg, rgba(6,182,212,0.08), transparent);
      &::before {
         content: '';
         position: absolute;
         left: 0;
         top: 0;
         bottom: 0;
         width: 3px;
         background: linear-gradient(180deg, #06b6d4, #8b5cf6);
         border-radius: 0 4px 4px 0;
      }
   `}

   &:hover {
      background: ${p => p.$active ? 'linear-gradient(90deg, rgba(6,182,212,0.08), transparent)' : '#f8fafc'};
   }

   [data-theme="dark"] & {
      &:hover { background: ${p => p.$active ? 'linear-gradient(90deg, rgba(6,182,212,0.12), transparent)' : '#1f2937'}; }
   }
`;

export const StepNumber = styled.div<{ $active?: boolean; $completed?: boolean }>`
   width: 30px;
   height: 30px;
   border-radius: 50%;
   display: flex;
   align-items: center;
   justify-content: center;
   font-size: 12px;
   font-weight: 700;
   flex-shrink: 0;
   transition: all 0.2s;

   ${p => p.$completed ? css`
      background: linear-gradient(135deg, #10b981, #059669);
      color: #fff;
   ` : p.$active ? css`
      background: linear-gradient(135deg, #06b6d4, #8b5cf6);
      color: #fff;
      box-shadow: 0 2px 8px rgba(6,182,212,0.3);
   ` : css`
      background: #f1f5f9;
      color: #94a3b8;
      [data-theme="dark"] & { background: #1f2937; color: #6b7280; }
   `}
`;

export const StepLabel = styled.div<{ $active?: boolean }>`
   font-size: 13px;
   font-weight: ${p => p.$active ? '600' : '500'};
   color: ${p => p.$active ? '#0e7490' : '#64748b'};
   line-height: 1.3;

   [data-theme="dark"] & {
      color: ${p => p.$active ? '#22d3ee' : '#9ca3af'};
   }
`;

/* ======================== MAIN CONTENT ======================== */
export const MainContent = styled.div`
   flex: 1;
   overflow-y: auto;
   padding: 32px 40px;
   animation: ${fadeIn} 0.4s ease;

   &::-webkit-scrollbar { width: 6px; }
   &::-webkit-scrollbar-track { background: transparent; }
   &::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
`;

export const SectionHeader = styled.div`
   margin-bottom: 28px;
`;

export const SectionTitle = styled.h2`
   font-size: 22px;
   font-weight: 700;
   color: #1e293b;
   margin-bottom: 6px;
   display: flex;
   align-items: center;
   gap: 10px;

   .icon {
      width: 32px;
      height: 32px;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 15px;
   }

   [data-theme="dark"] & { color: #f1f5f9; }
`;

export const SectionSubtitle = styled.p`
   font-size: 14px;
   color: #64748b;
   line-height: 1.5;

   [data-theme="dark"] & { color: #9ca3af; }
`;

/* ======================== HERO / WELCOME ======================== */
export const HeroBanner = styled.div`
   position: relative;
   background: linear-gradient(135deg, #0f172a 0%, #1e293b 40%, #0f172a 100%);
   border-radius: 16px;
   padding: 40px 44px;
   margin-bottom: 32px;
   overflow: hidden;
   animation: ${fadeIn} 0.5s ease;

   &::before {
      content: '';
      position: absolute;
      top: -50%;
      right: -20%;
      width: 400px;
      height: 400px;
      background: radial-gradient(circle, rgba(6,182,212,0.15) 0%, transparent 70%);
      pointer-events: none;
   }

   &::after {
      content: '';
      position: absolute;
      bottom: -30%;
      left: 10%;
      width: 300px;
      height: 300px;
      background: radial-gradient(circle, rgba(139,92,246,0.1) 0%, transparent 70%);
      pointer-events: none;
   }
`;

export const HeroContent = styled.div`
   position: relative;
   z-index: 1;
   display: flex;
   justify-content: space-between;
   align-items: center;
`;

export const HeroText = styled.div`
   max-width: 520px;

   h1 {
      font-size: 28px;
      font-weight: 800;
      color: #fff;
      margin-bottom: 12px;
      letter-spacing: -0.5px;
      line-height: 1.2;

      span {
         background: linear-gradient(135deg, #06b6d4, #8b5cf6, #ec4899);
         background-size: 200% auto;
         -webkit-background-clip: text;
         -webkit-text-fill-color: transparent;
         animation: ${shimmer} 3s linear infinite;
      }
   }

   p {
      font-size: 14px;
      color: #94a3b8;
      line-height: 1.7;
      margin-bottom: 20px;
   }
`;

export const HeroVisual = styled.div`
   position: relative;
   width: 180px;
   height: 180px;
   flex-shrink: 0;

   .orbit-ring {
      position: absolute;
      inset: 0;
      border: 1px dashed rgba(6,182,212,0.25);
      border-radius: 50%;
      animation: ${orbitSlow} 20s linear infinite;
   }

   .orbit-ring-inner {
      position: absolute;
      inset: 25px;
      border: 1px dashed rgba(139,92,246,0.2);
      border-radius: 50%;
      animation: ${orbitSlow} 15s linear infinite reverse;
   }

   .center-icon {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 60px;
      height: 60px;
      background: linear-gradient(135deg, #06b6d4, #8b5cf6);
      border-radius: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 24px;
      color: #fff;
      box-shadow: 0 8px 32px rgba(6,182,212,0.3);
      animation: ${float} 4s ease-in-out infinite;
   }

   .node-dot {
      position: absolute;
      width: 10px;
      height: 10px;
      border-radius: 50%;
      animation: ${pulse} 2s ease-in-out infinite;

      &.dot-1 { top: 0; left: 50%; transform: translateX(-50%); background: #06b6d4; animation-delay: 0s; }
      &.dot-2 { top: 50%; right: 0; transform: translateY(-50%); background: #8b5cf6; animation-delay: 0.5s; }
      &.dot-3 { bottom: 0; left: 50%; transform: translateX(-50%); background: #ec4899; animation-delay: 1s; }
      &.dot-4 { top: 50%; left: 0; transform: translateY(-50%); background: #f59e0b; animation-delay: 1.5s; }
   }
`;

export const HeroBadge = styled.span`
   display: inline-flex;
   align-items: center;
   gap: 6px;
   padding: 4px 12px;
   background: rgba(6,182,212,0.15);
   border: 1px solid rgba(6,182,212,0.3);
   border-radius: 20px;
   font-size: 11px;
   font-weight: 600;
   color: #22d3ee;
   margin-bottom: 16px;
   letter-spacing: 0.5px;
`;

/* ======================== CARDS GRID ======================== */
export const CardsGrid = styled.div`
   display: grid;
   grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
   gap: 16px;
   margin-bottom: 28px;
`;

export const FeatureCard = styled.div<{ $color?: string }>`
   background: #fff;
   border: 1px solid #e2e8f0;
   border-radius: 12px;
   padding: 20px;
   cursor: pointer;
   transition: all 0.25s;
   animation: ${scaleIn} 0.3s ease;

   &:hover {
      transform: translateY(-3px);
      box-shadow: 0 8px 24px rgba(0,0,0,0.06);
      border-color: ${p => p.$color || '#06b6d4'};
   }

   .card-icon {
      width: 40px;
      height: 40px;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 18px;
      margin-bottom: 14px;
      background: ${p => p.$color ? `${p.$color}15` : 'rgba(6,182,212,0.08)'};
      color: ${p => p.$color || '#06b6d4'};
   }

   h3 {
      font-size: 14px;
      font-weight: 700;
      color: #1e293b;
      margin-bottom: 6px;
   }

   p {
      font-size: 12px;
      color: #64748b;
      line-height: 1.5;
   }

   [data-theme="dark"] & {
      background: #1e293b;
      border-color: #334155;
      h3 { color: #f1f5f9; }
      p { color: #94a3b8; }
      &:hover { border-color: ${p => p.$color || '#06b6d4'}; box-shadow: 0 8px 24px rgba(0,0,0,0.3); }
   }
`;

/* ======================== FORM SECTIONS ======================== */
export const FormSection = styled.div`
   background: #fff;
   border: 1px solid #e2e8f0;
   border-radius: 12px;
   padding: 24px;
   margin-bottom: 20px;
   animation: ${fadeIn} 0.4s ease;

   [data-theme="dark"] & {
      background: #1e293b;
      border-color: #334155;
   }
`;

export const FormLabel = styled.label`
   display: block;
   font-size: 13px;
   font-weight: 600;
   color: #334155;
   margin-bottom: 8px;
   letter-spacing: 0.2px;

   .required {
      color: #ef4444;
      margin-left: 2px;
   }

   [data-theme="dark"] & { color: #e2e8f0; }
`;

export const FormHint = styled.div`
   font-size: 11px;
   color: #94a3b8;
   margin-top: 6px;
   line-height: 1.4;
`;

export const InlineValidationMessage = styled.div`
   display: flex;
   align-items: center;
   gap: 6px;
   margin-top: 8px;
   padding: 8px 10px;
   border-radius: 8px;
   font-size: 12px;
   font-weight: 500;
   color: #b91c1c;
   background: #fef2f2;
   border: 1px solid #fecaca;
   animation: ${fadeIn} 0.2s ease;

   [data-theme="dark"] & {
      color: #fca5a5;
      background: rgba(127,29,29,0.22);
      border-color: rgba(239,68,68,0.35);
   }
`;

export const FormInput = styled.input`
   width: 100%;
   padding: 10px 14px;
   border: 1.5px solid #e2e8f0;
   border-radius: 8px;
   font-size: 13px;
   color: #334155;
   background: #fff;
   transition: all 0.2s;
   font-family: inherit;

   &::placeholder { color: #94a3b8; }
   &:focus { border-color: #06b6d4; box-shadow: 0 0 0 3px rgba(6,182,212,0.1); outline: none; }

   [data-theme="dark"] & {
      background: #0f172a;
      border-color: #374151;
      color: #e2e8f0;
      &:focus { border-color: #06b6d4; box-shadow: 0 0 0 3px rgba(6,182,212,0.15); }
   }
`;

export const FormTextarea = styled.textarea`
   width: 100%;
   padding: 12px 14px;
   border: 1.5px solid #e2e8f0;
   border-radius: 8px;
   font-size: 13px;
   color: #334155;
   background: #fff;
   transition: all 0.2s;
   font-family: inherit;
   resize: vertical;
   min-height: 100px;
   line-height: 1.6;

   &::placeholder { color: #94a3b8; }
   &:focus { border-color: #06b6d4; box-shadow: 0 0 0 3px rgba(6,182,212,0.1); outline: none; }

   [data-theme="dark"] & {
      background: #0f172a;
      border-color: #374151;
      color: #e2e8f0;
      &:focus { border-color: #06b6d4; box-shadow: 0 0 0 3px rgba(6,182,212,0.15); }
   }
`;

export const FormSelect = styled.select`
   width: 100%;
   padding: 10px 14px;
   border: 1.5px solid #e2e8f0;
   border-radius: 8px;
   font-size: 13px;
   color: #334155;
   background: #fff;
   transition: all 0.2s;
   font-family: inherit;
   cursor: pointer;
   appearance: none;
   background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
   background-repeat: no-repeat;
   background-position: right 12px center;
   padding-right: 36px;

   &:focus { border-color: #06b6d4; box-shadow: 0 0 0 3px rgba(6,182,212,0.1); outline: none; }

   [data-theme="dark"] & {
      background-color: #0f172a;
      border-color: #374151;
      color: #e2e8f0;
   }
`;

export const FormRow = styled.div`
   display: grid;
   grid-template-columns: 1fr 1fr;
   gap: 16px;
   margin-bottom: 16px;

   @media (max-width: 768px) {
      grid-template-columns: 1fr;
   }
`;

export const FormGroup = styled.div`
   margin-bottom: 16px;
   &:last-child { margin-bottom: 0; }
`;

/* ======================== PERSONALITY DESIGNER ======================== */
export const MoodGrid = styled.div`
   display: grid;
   grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
   gap: 12px;
   margin-top: 8px;
`;

export const MoodCard = styled.div<{ $active?: boolean; $color?: string }>`
   display: flex;
   flex-direction: column;
   align-items: center;
   gap: 8px;
   padding: 18px 14px;
   border: 2px solid ${p => p.$active ? (p.$color || '#06b6d4') : '#e2e8f0'};
   border-radius: 12px;
   cursor: pointer;
   transition: all 0.25s;
   background: ${p => p.$active ? `${p.$color || '#06b6d4'}08` : '#fff'};
   position: relative;

   ${p => p.$active && css`
      box-shadow: 0 4px 16px ${p.$color || '#06b6d4'}20;
      &::after {
         content: '✓';
         position: absolute;
         top: 6px;
         right: 8px;
         font-size: 12px;
         color: ${p.$color || '#06b6d4'};
         font-weight: 700;
      }
   `}

   &:hover {
      border-color: ${p => p.$color || '#06b6d4'};
      transform: translateY(-2px);
   }

   .mood-icon {
      font-size: 28px;
   }

   .mood-label {
      font-size: 13px;
      font-weight: 600;
      color: #334155;
   }

   .mood-desc {
      font-size: 11px;
      color: #94a3b8;
      text-align: center;
      line-height: 1.3;
   }

   [data-theme="dark"] & {
      background: ${p => p.$active ? `${p.$color || '#06b6d4'}12` : '#0f172a'};
      border-color: ${p => p.$active ? (p.$color || '#06b6d4') : '#334155'};
      .mood-label { color: #e2e8f0; }
      .mood-desc { color: #6b7280; }
   }
`;

export const ToneSlider = styled.div`
   margin-top: 16px;

   .slider-label {
      display: flex;
      justify-content: space-between;
      font-size: 11px;
      color: #94a3b8;
      margin-top: 6px;
   }

   input[type="range"] {
      width: 100%;
      height: 6px;
      border-radius: 3px;
      appearance: none;
      background: linear-gradient(90deg, #06b6d4, #8b5cf6);
      outline: none;
      margin-top: 8px;

      &::-webkit-slider-thumb {
         appearance: none;
         width: 18px;
         height: 18px;
         border-radius: 50%;
         background: #fff;
         border: 2px solid #06b6d4;
         cursor: pointer;
         box-shadow: 0 2px 6px rgba(0,0,0,0.15);
      }
   }
`;

/* ======================== PROMPT ASSISTANT ======================== */
export const PromptAssistantBox = styled.div`
   background: linear-gradient(135deg, #f0f9ff, #f5f3ff);
   border: 1px dashed #06b6d4;
   border-radius: 12px;
   padding: 20px;
   margin-top: 12px;
   animation: ${slideInRight} 0.4s ease;

   .assistant-header {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 12px;
      font-size: 13px;
      font-weight: 600;
      color: #0e7490;
   }

   .expanded-prompt {
      background: #fff;
      border: 1px solid #e0f2fe;
      border-radius: 8px;
      padding: 14px;
      font-size: 13px;
      color: #334155;
      line-height: 1.7;
      white-space: pre-wrap;
      max-height: 200px;
      overflow-y: auto;
   }

   .assistant-actions {
      display: flex;
      gap: 8px;
      margin-top: 12px;
   }

   [data-theme="dark"] & {
      background: linear-gradient(135deg, rgba(6,182,212,0.05), rgba(139,92,246,0.05));
      border-color: #164e63;
      .assistant-header { color: #22d3ee; }
      .expanded-prompt { background: #0f172a; border-color: #1e3a5f; color: #e2e8f0; }
   }
`;

export const SmallBtn = styled.button<{ $variant?: 'primary' | 'secondary' | 'success' | 'danger' }>`
   display: inline-flex;
   align-items: center;
   gap: 5px;
   padding: 6px 14px;
   border-radius: 6px;
   font-size: 12px;
   font-weight: 600;
   border: none;
   cursor: pointer;
   transition: all 0.2s;

   ${p => {
      switch(p.$variant) {
         case 'primary': return css`
            background: linear-gradient(135deg, #06b6d4, #0891b2);
            color: #fff;
            &:hover { box-shadow: 0 2px 8px rgba(6,182,212,0.3); }
         `;
         case 'success': return css`
            background: linear-gradient(135deg, #10b981, #059669);
            color: #fff;
            &:hover { box-shadow: 0 2px 8px rgba(16,185,129,0.3); }
         `;
         case 'danger': return css`
            background: linear-gradient(135deg, #ef4444, #dc2626);
            color: #fff;
            &:hover { box-shadow: 0 2px 8px rgba(239,68,68,0.3); }
         `;
         default: return css`
            background: #f1f5f9;
            color: #475569;
            border: 1px solid #e2e8f0;
            &:hover { background: #e2e8f0; }
            [data-theme="dark"] & { background: #1f2937; color: #d1d5db; border-color: #374151; }
         `;
      }
   }}
`;

/* ======================== SIMULATOR ======================== */
export const SimulatorContainer = styled.div`
   display: flex;
   flex-direction: column;
   gap: 20px;
`;

export const SimulatorControls = styled.div`
   display: flex;
   align-items: flex-end;
   gap: 16px;
   flex-wrap: wrap;
`;

export const SimulatorResults = styled.div`
   animation: ${fadeIn} 0.5s ease;
`;

export const MetricsGrid = styled.div`
   display: grid;
   grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
   gap: 14px;
   margin-bottom: 20px;
`;

export const MetricCard = styled.div<{ $color?: string }>`
   background: #fff;
   border: 1px solid #e2e8f0;
   border-radius: 10px;
   padding: 16px;
   position: relative;
   overflow: hidden;

   &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 3px;
      background: ${p => p.$color || '#06b6d4'};
   }

   .metric-label {
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: #94a3b8;
      margin-bottom: 6px;
   }

   .metric-value {
      font-size: 24px;
      font-weight: 800;
      color: ${p => p.$color || '#06b6d4'};
      margin-bottom: 4px;
   }

   .metric-bar {
      width: 100%;
      height: 4px;
      background: #f1f5f9;
      border-radius: 2px;
      overflow: hidden;

      .fill {
         height: 100%;
         background: ${p => p.$color || '#06b6d4'};
         border-radius: 2px;
         animation: ${progressFill} 1s ease;
      }
   }

   [data-theme="dark"] & {
      background: #1e293b;
      border-color: #334155;
      .metric-bar { background: #334155; }
   }
`;

export const ConversationList = styled.div`
   display: flex;
   flex-direction: column;
   gap: 8px;
   max-height: 600px;
   overflow-y: auto;
   padding-right: 4px;

   &::-webkit-scrollbar { width: 4px; }
   &::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
`;

export const ConversationItem = styled.div<{ $expanded?: boolean }>`
   background: #fff;
   border: 1px solid #e2e8f0;
   border-radius: 8px;
   overflow: hidden;
   transition: all 0.2s;

   .conv-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 12px 16px;
      cursor: pointer;
      transition: background 0.2s;

      &:hover { background: #f8fafc; }

      .conv-user {
         display: flex;
         align-items: center;
         gap: 8px;
         font-size: 13px;
         font-weight: 500;
         color: #334155;
      }

      .conv-badge {
         display: flex;
         align-items: center;
         gap: 6px;
      }
   }

   .conv-body {
      padding: 0 16px 14px;
      border-top: 1px solid #f1f5f9;
      animation: ${fadeIn} 0.3s ease;

      .conv-message {
         padding: 10px 0;
         font-size: 13px;
         line-height: 1.6;
         color: #475569;

         &.user-msg {
            font-weight: 500;
            color: #1e293b;
            &::before { content: 'Q: '; color: #06b6d4; font-weight: 700; }
         }

         &.agent-msg {
            padding-left: 12px;
            border-left: 2px solid #e0f2fe;
            &::before { content: 'A: '; color: #8b5cf6; font-weight: 700; }
         }
      }
   }

   [data-theme="dark"] & {
      background: #1e293b;
      border-color: #334155;
      .conv-header { &:hover { background: #1f2937; } .conv-user { color: #e2e8f0; } }
      .conv-body { border-top-color: #334155; .conv-message { color: #d1d5db; &.user-msg { color: #f1f5f9; } } }
   }
`;

export const RatingBadge = styled.span<{ $type: 'good' | 'bad' | 'neutral' }>`
   display: inline-flex;
   align-items: center;
   gap: 3px;
   padding: 2px 8px;
   border-radius: 4px;
   font-size: 11px;
   font-weight: 600;

   ${p => {
      switch(p.$type) {
         case 'good': return css`background: #dcfce7; color: #16a34a;`;
         case 'bad': return css`background: #fee2e2; color: #dc2626;`;
         default: return css`background: #fef3c7; color: #d97706;`;
      }
   }}
`;

/* ======================== SELF-IMPROVEMENT ======================== */
export const FeedbackList = styled.div`
   display: flex;
   flex-direction: column;
   gap: 10px;
`;

export const FeedbackItem = styled.div`
   background: #fff;
   border: 1px solid #e2e8f0;
   border-radius: 10px;
   padding: 16px;
   display: flex;
   gap: 14px;
   align-items: flex-start;
   animation: ${fadeIn} 0.3s ease;

   .feedback-icon {
      width: 36px;
      height: 36px;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      font-size: 16px;
   }

   .feedback-content {
      flex: 1;

      .feedback-original {
         font-size: 13px;
         color: #64748b;
         margin-bottom: 6px;
         text-decoration: line-through;
      }

      .feedback-improved {
         font-size: 13px;
         color: #1e293b;
         font-weight: 500;
         padding: 8px 12px;
         background: #f0fdf4;
         border-left: 3px solid #10b981;
         border-radius: 0 6px 6px 0;
      }

      .feedback-label {
         font-size: 11px;
         color: #94a3b8;
         margin-top: 6px;
      }
   }

   [data-theme="dark"] & {
      background: #1e293b;
      border-color: #334155;
      .feedback-content {
         .feedback-original { color: #6b7280; }
         .feedback-improved { background: rgba(16,185,129,0.08); color: #e2e8f0; border-color: #059669; }
      }
   }
`;

/* ======================== EXPORT SECTION ======================== */
export const ExportContainer = styled.div`
   text-align: center;
   padding: 40px 20px;
`;

export const ExportVisual = styled.div`
   position: relative;
   width: 320px;
   height: 180px;
   margin: 0 auto 28px;
   display: flex;
   align-items: center;
   justify-content: center;

   .export-from {
      width: 100px;
      height: 100px;
      background: linear-gradient(135deg, #06b6d4, #8b5cf6);
      border-radius: 16px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      color: #fff;
      font-size: 24px;
      box-shadow: 0 8px 24px rgba(6,182,212,0.25);
      animation: ${float} 3s ease-in-out infinite;

      span {
         font-size: 10px;
         font-weight: 600;
         margin-top: 4px;
         opacity: 0.9;
      }
   }

   .export-arrow {
      display: flex;
      align-items: center;
      margin: 0 16px;
      color: #94a3b8;
      font-size: 24px;
      animation: ${pulse} 2s ease-in-out infinite;
   }

   .export-to {
      width: 100px;
      height: 100px;
      border: 2px dashed #06b6d4;
      border-radius: 16px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      color: #06b6d4;
      font-size: 24px;
      background: rgba(6,182,212,0.04);

      span {
         font-size: 10px;
         font-weight: 600;
         margin-top: 4px;
      }
   }
`;

export const ExportButton = styled.button`
   display: inline-flex;
   align-items: center;
   gap: 10px;
   padding: 14px 36px;
   background: linear-gradient(135deg, #06b6d4 0%, #8b5cf6 100%);
   color: #fff;
   border: none;
   border-radius: 12px;
   font-size: 15px;
   font-weight: 700;
   cursor: pointer;
   transition: all 0.3s;
   box-shadow: 0 4px 16px rgba(6,182,212,0.3);
   letter-spacing: 0.3px;

   &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 32px rgba(6,182,212,0.4);
   }

   &:active {
      transform: translateY(0);
   }

   &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
   }
`;

export const ExportSummary = styled.div`
   display: grid;
   grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
   gap: 12px;
   margin: 24px auto;
   max-width: 700px;
   text-align: left;

   .summary-item {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 12px 16px;
      background: #f0fdf4;
      border: 1px solid #bbf7d0;
      border-radius: 8px;
      font-size: 13px;
      color: #166534;
      font-weight: 500;

      .check-icon {
         color: #16a34a;
         font-size: 16px;
         flex-shrink: 0;
      }
   }

   [data-theme="dark"] & .summary-item {
      background: rgba(16,185,129,0.08);
      border-color: rgba(16,185,129,0.2);
      color: #a7f3d0;
   }
`;

/* ======================== MISC UTILITIES ======================== */
export const Divider = styled.div`
   height: 1px;
   background: #e2e8f0;
   margin: 20px 0;
   [data-theme="dark"] & { background: #334155; }
`;

export const Tag = styled.span<{ $color?: string }>`
   display: inline-flex;
   align-items: center;
   gap: 4px;
   padding: 3px 10px;
   border-radius: 6px;
   font-size: 11px;
   font-weight: 600;
   background: ${p => p.$color ? `${p.$color}12` : '#f1f5f9'};
   color: ${p => p.$color || '#64748b'};
`;

export const LoadingDots = styled.div`
   display: flex;
   gap: 4px;
   align-items: center;
   justify-content: center;
   padding: 20px;
   
   span {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #06b6d4;
      animation: ${pulse} 1s ease-in-out infinite;

      &:nth-child(2) { animation-delay: 0.15s; }
      &:nth-child(3) { animation-delay: 0.3s; }
   }
`;

export const EmptyState = styled.div`
   text-align: center;
   padding: 40px 20px;
   color: #94a3b8;

   .empty-icon {
      font-size: 40px;
      margin-bottom: 12px;
      opacity: 0.4;
   }

   h4 {
      font-size: 15px;
      font-weight: 600;
      color: #64748b;
      margin-bottom: 6px;
   }

   p {
      font-size: 13px;
      line-height: 1.5;
   }
`;

export const OutputFormatGrid = styled.div`
   display: grid;
   grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
   gap: 10px;
   margin-top: 8px;
`;

export const OutputFormatCard = styled.div<{ $active?: boolean }>`
   display: flex;
   flex-direction: column;
   align-items: center;
   gap: 6px;
   padding: 14px 10px;
   border: 2px solid ${p => p.$active ? '#06b6d4' : '#e2e8f0'};
   border-radius: 10px;
   cursor: pointer;
   transition: all 0.2s;
   background: ${p => p.$active ? 'rgba(6,182,212,0.04)' : '#fff'};

   &:hover {
      border-color: #06b6d4;
      transform: translateY(-2px);
   }

   .format-icon { font-size: 20px; }
   .format-label { font-size: 12px; font-weight: 600; color: #334155; }

   [data-theme="dark"] & {
      background: ${p => p.$active ? 'rgba(6,182,212,0.08)' : '#0f172a'};
      border-color: ${p => p.$active ? '#06b6d4' : '#334155'};
      .format-label { color: #e2e8f0; }
   }
`;

/* Temperature visual slider */
export const TemperatureVisual = styled.div`
   display: flex;
   align-items: center;
   gap: 16px;
   margin-top: 8px;

   .temp-label-left, .temp-label-right {
      font-size: 11px;
      color: #94a3b8;
      min-width: 60px;
   }
   .temp-label-right { text-align: right; }

   .temp-bar {
      flex: 1;
      position: relative;

      input[type="range"] {
         width: 100%;
         height: 8px;
         border-radius: 4px;
         appearance: none;
         background: linear-gradient(90deg, #3b82f6, #f59e0b, #ef4444);
         outline: none;

         &::-webkit-slider-thumb {
            appearance: none;
            width: 20px;
            height: 20px;
            border-radius: 50%;
            background: #fff;
            border: 2px solid #06b6d4;
            box-shadow: 0 2px 8px rgba(0,0,0,0.15);
            cursor: pointer;
         }
      }
   }

   .temp-value {
      font-size: 16px;
      font-weight: 700;
      color: #06b6d4;
      min-width: 36px;
      text-align: center;
   }
`;

/* ======================== SAVED AGENTS PANEL ======================== */
export const SavedAgentsPanel = styled.div`
   width: 260px;
   background: #fff;
   border-left: 1px solid #e2e8f0;
   display: flex;
   flex-direction: column;
   flex-shrink: 0;
   overflow: hidden;

   [data-theme="dark"] & {
      background: #111827;
      border-left-color: #1f2937;
   }
`;

export const PanelHeader = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   padding: 16px 16px 12px;
   border-bottom: 1px solid #e2e8f0;

   > span {
      font-size: 11px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 1.2px;
      color: #94a3b8;
   }

   [data-theme="dark"] & {
      border-bottom-color: #1f2937;
   }
`;

export const SavedAgentsList = styled.div`
   flex: 1;
   overflow-y: auto;
   padding: 8px;

   &::-webkit-scrollbar { width: 4px; }
   &::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
`;

export const SavedAgentCard = styled.div<{ $active?: boolean }>`
   padding: 12px;
   border: 1.5px solid ${p => p.$active ? '#06b6d4' : '#e2e8f0'};
   border-radius: 10px;
   cursor: pointer;
   transition: all 0.2s;
   margin-bottom: 6px;
   background: ${p => p.$active ? 'rgba(6,182,212,0.04)' : 'transparent'};

   &:hover {
      border-color: #06b6d4;
      background: rgba(6,182,212,0.02);
   }

   .agent-name {
      font-size: 13px;
      font-weight: 600;
      color: #1e293b;
      margin-bottom: 4px;
      display: flex;
      align-items: center;
      justify-content: space-between;
   }

   .agent-meta {
      font-size: 11px;
      color: #94a3b8;
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
   }

   .agent-actions {
      display: flex;
      gap: 4px;
      margin-top: 6px;
   }

   .agent-action-btn {
      padding: 2px 8px;
      font-size: 10px;
      font-weight: 600;
      border-radius: 4px;
      border: 1px solid #e2e8f0;
      background: transparent;
      color: #64748b;
      cursor: pointer;
      transition: all 0.15s;
      
      &:hover { background: #f1f5f9; color: #334155; }
      &.delete:hover { background: #fee2e2; color: #dc2626; border-color: #fca5a5; }
   }

   [data-theme="dark"] & {
      border-color: ${p => p.$active ? '#06b6d4' : '#334155'};
      background: ${p => p.$active ? 'rgba(6,182,212,0.08)' : 'transparent'};
      .agent-name { color: #f1f5f9; }
      .agent-meta { color: #6b7280; }
      .agent-action-btn {
         border-color: #374151;
         color: #9ca3af;
         &:hover { background: #1f2937; color: #d1d5db; }
      }
   }
`;

/* ======================== EXPORT MODAL / WORKFLOW PICKER ======================== */
export const ExportModeSelector = styled.div`
   display: grid;
   grid-template-columns: 1fr 1fr;
   gap: 16px;
   margin-bottom: 24px;
`;

export const ExportModeCard = styled.div<{ $active?: boolean; $color?: string }>`
   display: flex;
   flex-direction: column;
   align-items: center;
   gap: 10px;
   padding: 24px 16px;
   border: 2px solid ${p => p.$active ? (p.$color || '#06b6d4') : '#e2e8f0'};
   border-radius: 12px;
   cursor: pointer;
   transition: all 0.25s;
   background: ${p => p.$active ? `${p.$color || '#06b6d4'}06` : '#fff'};

   &:hover {
      border-color: ${p => p.$color || '#06b6d4'};
      transform: translateY(-2px);
      box-shadow: 0 4px 16px rgba(0,0,0,0.06);
   }

   .mode-icon {
      width: 48px;
      height: 48px;
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 20px;
      background: ${p => p.$color ? `${p.$color}12` : 'rgba(6,182,212,0.08)'};
      color: ${p => p.$color || '#06b6d4'};
   }

   .mode-label {
      font-size: 14px;
      font-weight: 600;
      color: #1e293b;
   }

   .mode-desc {
      font-size: 12px;
      color: #64748b;
      text-align: center;
      line-height: 1.4;
   }

   [data-theme="dark"] & {
      background: ${p => p.$active ? `${p.$color || '#06b6d4'}10` : '#1e293b'};
      border-color: ${p => p.$active ? (p.$color || '#06b6d4') : '#334155'};
      .mode-label { color: #f1f5f9; }
      .mode-desc { color: #9ca3af; }
   }
`;

export const WorkflowPickerList = styled.div`
   max-height: 300px;
   overflow-y: auto;
   border: 1px solid #e2e8f0;
   border-radius: 10px;
   margin-bottom: 16px;

   &::-webkit-scrollbar { width: 4px; }
   &::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }

   [data-theme="dark"] & { border-color: #334155; }
`;

export const WorkflowPickerItem = styled.div<{ $active?: boolean }>`
   display: flex;
   align-items: center;
   justify-content: space-between;
   padding: 12px 16px;
   cursor: pointer;
   transition: all 0.15s;
   border-bottom: 1px solid #f1f5f9;
   background: ${p => p.$active ? 'rgba(6,182,212,0.06)' : 'transparent'};
   border-left: 3px solid ${p => p.$active ? '#06b6d4' : 'transparent'};

   &:last-child { border-bottom: none; }
   &:hover { background: rgba(6,182,212,0.04); }

   .wf-info {
      flex: 1;
      min-width: 0;

      .wf-name {
         font-size: 13px;
         font-weight: 600;
         color: #1e293b;
         white-space: nowrap;
         overflow: hidden;
         text-overflow: ellipsis;
      }

      .wf-meta {
         font-size: 11px;
         color: #94a3b8;
         margin-top: 2px;
      }
   }

   .wf-check {
      width: 20px;
      height: 20px;
      border-radius: 50%;
      border: 2px solid ${p => p.$active ? '#06b6d4' : '#cbd5e1'};
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      margin-left: 12px;
      background: ${p => p.$active ? '#06b6d4' : 'transparent'};
      color: #fff;
      font-size: 10px;
      transition: all 0.15s;
   }

   [data-theme="dark"] & {
      border-bottom-color: #1f2937;
      .wf-info .wf-name { color: #f1f5f9; }
      .wf-check { border-color: ${p => p.$active ? '#06b6d4' : '#4b5563'}; }
   }
`;

/* ======================== IMPROVEMENT PREVIEW ======================== */
export const ImprovementPreview = styled.div`
   background: #fff;
   border: 1px solid #e2e8f0;
   border-radius: 12px;
   padding: 24px;
   margin-top: 16px;
   animation: ${fadeIn} 0.3s ease;

   .preview-header {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 14px;
      font-weight: 600;
      color: #1e293b;
      margin-bottom: 16px;
      svg { color: #8b5cf6; }
   }

   .preview-section {
      margin-bottom: 16px;

      .preview-label {
         font-size: 11px;
         font-weight: 600;
         text-transform: uppercase;
         letter-spacing: 0.5px;
         color: #94a3b8;
         margin-bottom: 8px;
      }

      .preview-content {
         font-size: 13px;
         line-height: 1.7;
         color: #475569;
         background: #f8fafc;
         border: 1px solid #e2e8f0;
         border-radius: 8px;
         padding: 14px;
         white-space: pre-wrap;
         max-height: 200px;
         overflow-y: auto;
         font-family: 'Inter', system-ui, sans-serif;
      }

      &.improved .preview-content {
         background: #f0fdf4;
         border-color: #bbf7d0;
      }

      &.current .preview-content {
         background: #fef2f2;
         border-color: #fecaca;
      }
   }

   .preview-actions {
      display: flex;
      gap: 10px;
      justify-content: flex-end;
      margin-top: 16px;
      padding-top: 16px;
      border-top: 1px solid #f1f5f9;
   }

   [data-theme="dark"] & {
      background: #1e293b;
      border-color: #334155;
      .preview-header { color: #f1f5f9; }
      .preview-section .preview-content {
         background: #0f172a;
         border-color: #334155;
         color: #cbd5e1;
      }
      .preview-section.improved .preview-content {
         background: rgba(16,185,129,0.08);
         border-color: #065f46;
      }
      .preview-section.current .preview-content {
         background: rgba(239,68,68,0.08);
         border-color: #7f1d1d;
      }
      .preview-actions { border-top-color: #334155; }
   }
`;

export const FeedbackActions = styled.div`
   display: flex;
   gap: 6px;
   margin-top: 8px;
`;

/* ======================== LANDING LIBRARY ======================== */
export const LandingWrap = styled.div`
   animation: ${fadeIn} 0.5s ease-out;
   padding: 1.5rem;
   height: calc(100vh - 60px);
   overflow-y: auto;
`;

export const LandingHeader = styled.div`
   display: flex;
   justify-content: space-between;
   align-items: center;
   margin-bottom: 2.5rem;
   background: linear-gradient(120deg, #ffffff, #f1f5f9);
   padding: 1.5rem;
   border-radius: 1rem;
   box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
   gap: 1rem;

   @media (max-width: 900px) {
      flex-direction: column;
      align-items: stretch;
   }

   [data-theme="dark"] & {
      background: linear-gradient(120deg, #0f172a, #111827);
      box-shadow: none;
      border: 1px solid #1f2937;
   }
`;

export const LandingTitleBlock = styled.div`
   h1 {
      font-size: 1.75rem;
      font-weight: 700;
      margin: 0;
      background: linear-gradient(to right, #4361ee, #4cc9f0);
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
   }

   p {
      font-size: 0.95rem;
      margin: 0.5rem 0 0;
      color: #64748b;
   }

   [data-theme="dark"] & {
      h1 { color: #f1f5f9; }
      p { color: #9ca3af; }
   }
`;

export const LandingCreateBtn = styled.button`
   border: none;
   border-radius: 0.5rem;
   background: linear-gradient(to right, #4361ee, #4cc9f0);
   color: #fff;
   font-weight: 600;
   font-size: 0.875rem;
   padding: 0.75rem 1.5rem;
   display: inline-flex;
   align-items: center;
   gap: 0.5rem;
   cursor: pointer;
   transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
   box-shadow: 0 4px 12px rgba(67, 97, 238, 0.3);

   &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 16px rgba(67, 97, 238, 0.4);
   }
`;

export const LandingStats = styled.div`
   display: grid;
   grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
   gap: 1rem;
   margin-bottom: 2rem;

   @media (max-width: 980px) {
      grid-template-columns: 1fr;
   }
`;

export const LandingStatCard = styled.div`
   background: linear-gradient(135deg, #ffffff, #f8fafc);
   border-radius: 1rem;
   padding: 1.25rem;
   display: flex;
   align-items: center;
   gap: 1rem;
   box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
   border: 1px solid #e2e8f0;
   transition: all 0.3s;

   &:hover {
      transform: translateY(-3px);
      box-shadow: 0 10px 24px rgba(2, 132, 199, 0.12);
   }

   .stat-icon {
      width: 2.5rem;
      height: 2.5rem;
      border-radius: 0.75rem;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #4361ee;
      background: rgba(67, 97, 238, 0.08);
      font-size: 1rem;
   }

   h3 {
      margin: 0;
      font-size: 1.5rem;
      color: #0f172a;
      line-height: 1.2;
   }

   p {
      margin: 0;
      font-size: 0.875rem;
      color: #64748b;
   }

   [data-theme="dark"] & {
      background: #111827;
      border-color: #1f2937;
      h3 { color: #e2e8f0; }
      p { color: #9ca3af; }
      .stat-icon { color: #22d3ee; background: rgba(34,211,238,0.14); }
   }
`;

export const LandingSearchBar = styled.div`
   display: flex;
   align-items: center;
   gap: 0;
   position: relative;
   margin-bottom: 2rem;

   svg {
      position: absolute;
      left: 1rem;
      top: 50%;
      transform: translateY(-50%);
      width: 1.25rem;
      height: 1.25rem;
      color: #64748b;
      pointer-events: none;
      z-index: 2;
      transition: all 0.3s;
   }

   &:focus-within svg {
      transform: translateY(-12px);
   }

   input {
      width: 100%;
      padding: 1rem 1rem 1rem 3rem;
      border-radius: 0.75rem;
      border: 2px solid #e2e8f0;
      background: #fff;
      color: #0f172a;
      font-size: 1rem;
      transition: all 0.3s;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
   }

   input:focus {
      outline: none;
      border-color: #4361ee;
      box-shadow: 0 0 0 3px rgba(67, 97, 238, 0.12), 0 4px 12px rgba(0, 0, 0, 0.05);
      transform: translateY(-2px);
   }

   input::placeholder {
      color: #94a3b8;
   }

   [data-theme="dark"] & {
      input {
         border-color: #1f2937;
         background: #111827;
         color: #e5e7eb;
      }
      input::placeholder { color: #6b7280; }
      svg { color: #9ca3af; }
   }
`;

export const LandingFilters = styled.div`
   display: flex;
   gap: 0.5rem;
   margin-bottom: 1.5rem;
   overflow-x: auto;
   padding-bottom: 0.5rem;

   &::-webkit-scrollbar {
      height: 4px;
   }

   &::-webkit-scrollbar-thumb {
      background: #cbd5e1;
      border-radius: 4px;
   }
`;

export const LandingFilterBtn = styled.button<{ $active?: boolean }>`
   padding: 0.5rem 1rem;
   border-radius: 2rem;
   background-color: ${p => p.$active ? '#4361ee' : 'transparent'};
   color: ${p => p.$active ? '#fff' : '#64748b'};
   border: 1px solid ${p => p.$active ? '#4361ee' : '#e2e8f0'};
   font-size: 0.85rem;
   font-weight: 500;
   cursor: pointer;
   transition: all 0.2s;
   white-space: nowrap;

   &:hover {
      background-color: ${p => p.$active ? '#4361ee' : '#f8fafc'};
      border-color: #4361ee;
      color: ${p => p.$active ? '#fff' : '#4361ee'};
   }

   [data-theme="dark"] & {
      border-color: ${p => p.$active ? '#4361ee' : '#334155'};
      background: ${p => p.$active ? '#4361ee' : '#111827'};
      color: ${p => p.$active ? '#67e8f9' : '#cbd5e1'};
   }
`;

export const LandingGrid = styled.div`
   display: grid;
   grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
   gap: 1.5rem;
   margin-top: 1.5rem;
`;

export const LandingCard = styled.div`
   background: #fff;
   border: 1px solid #e2e8f0;
   border-radius: 1rem;
   padding: 0;
   cursor: pointer;
   overflow: hidden;
   position: relative;
   text-decoration: none;
   transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
   animation: ${fadeIn} 0.4s ease-out;

   &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 4px;
      background: var(--accent, #4361ee);
   }

   &:hover {
      transform: translateY(-6px) scale(1.01);
      box-shadow: 0 10px 24px rgba(15, 23, 42, 0.14);
   }

   .card-banner {
      height: 128px;
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: #080e1f;
      background-image: radial-gradient(circle at 1px 1px, rgba(255, 255, 255, 0.06) 1px, transparent 0);
      background-size: 18px 18px;
      overflow: hidden;

      &::after {
         content: '';
         position: absolute;
         top: 50%;
         left: 50%;
         transform: translate(-50%, -50%);
         width: 88px;
         height: 88px;
         border-radius: 50%;
         background: var(--accent, #4361ee);
         opacity: 0.15;
         filter: blur(24px);
         pointer-events: none;
      }
   }

   .banner-badge {
      position: absolute;
      top: 10px;
      right: 10px;
      font-size: 0.65rem;
      font-weight: 700;
      padding: 0.2rem 0.6rem;
      border-radius: 1rem;
      background: rgba(255, 255, 255, 0.1);
      color: rgba(255, 255, 255, 0.6);
      backdrop-filter: blur(4px);
      border: 1px solid rgba(255, 255, 255, 0.12);
      letter-spacing: 0.04em;
      text-transform: uppercase;

      &.active {
         background: rgba(16, 185, 129, 0.22);
         color: #4ade80;
         border-color: rgba(16, 185, 129, 0.35);
      }
   }

   .head {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      padding: 1.5rem 1.5rem 1rem;
   }

   .card-actions {
      position: relative;
      flex: 0 0 auto;
      margin-left: 0.75rem;
      z-index: 4;
   }

   .menu-btn {
      width: 30px;
      height: 30px;
      border-radius: 8px;
      border: 1px solid #e2e8f0;
      background: #fff;
      color: #64748b;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: all 0.2s ease;
   }

   .menu-btn:hover {
      color: #4361ee;
      border-color: #cbd5e1;
      background: #f8fafc;
   }

   .menu-pop {
      position: absolute;
      top: calc(100% + 8px);
      right: 0;
      min-width: 160px;
      display: flex;
      flex-direction: column;
      background: #fff;
      border: 1px solid #e2e8f0;
      border-radius: 10px;
      box-shadow: 0 14px 28px rgba(15, 23, 42, 0.16);
      overflow: hidden;
      z-index: 8;
   }

   .menu-item {
      border: 0;
      background: transparent;
      color: #334155;
      font-size: 0.83rem;
      font-weight: 500;
      padding: 0.62rem 0.8rem;
      text-align: left;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      cursor: pointer;
      transition: background 0.15s ease, color 0.15s ease;
   }

   .menu-item:hover {
      background: #f8fafc;
      color: #1e293b;
   }

   .menu-item.delete:hover {
      background: #fef2f2;
      color: #dc2626;
   }

   h4 {
      margin: 0;
      font-size: 1.25rem;
      font-weight: 700;
      color: #0f172a;
      display: -webkit-box;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
      overflow: hidden;
   }

   .time-row {
      margin-top: 0.25rem;
      font-size: 0.85rem;
      color: #64748b;
      display: flex;
      align-items: center;
      gap: 0.25rem;
   }

   .time-row svg {
      width: 0.85rem;
      height: 0.85rem;
      opacity: 0.6;
   }

   .stats-row {
      padding: 1rem 1.5rem;
      display: flex;
      align-items: center;
      gap: 0.75rem;
   }

   .dot {
      width: 0.75rem;
      height: 0.75rem;
      border-radius: 50%;
      background: #94a3b8;
   }

   .dot.active {
      background: #10b981;
      position: relative;
   }

   .dot.active::after {
      content: '';
      position: absolute;
      top: -4px;
      left: -4px;
      right: -4px;
      bottom: -4px;
      border-radius: 50%;
      background: rgba(16, 185, 129, 0.2);
      animation: ${pulse} 2s infinite;
   }

   .value-wrap {
      display: flex;
      flex-direction: column;
   }

   .value {
      font-size: 1.5rem;
      font-weight: 800;
      color: #0f172a;
      line-height: 1.1;
   }

   .label {
      font-size: 0.75rem;
      color: #64748b;
      margin-top: 0.25rem;
   }

   .meta {
      margin: 0.25rem 1.5rem 0.25rem;
      color: #64748b;
      font-size: 0.85rem;
   }

   .chips {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
      padding: 0 1.5rem 1.5rem;
      margin-top: 0.5rem;
   }

   .chips span {
      background: #f1f5f9;
      color: #64748b;
      font-size: 0.75rem;
      padding: 0.35rem 0.75rem;
      border-radius: 1rem;
   }

   .foot {
      padding: 0 1.5rem 1.2rem;
      display: flex;
      align-items: center;
      justify-content: space-between;
      color: #64748b;
      font-size: 0.8rem;
      font-weight: 500;
   }

   .iter-info {
      font-size: 0.72rem;
      color: #94a3b8;
      font-weight: 500;
   }

   .foot span:last-child {
      color: #0891b2;
      display: inline-flex;
      align-items: center;
      gap: 6px;
   }

   [data-theme="dark"] & {
      background: #111827;
      border-color: #1f2937;
      h4 { color: #f8fafc; }
      .meta, .foot, .time-row, .label { color: #94a3b8; }
      .value { color: #f1f5f9; }
      .chips span { background: #1f2937; color: #cbd5e1; }
      .foot span:last-child { color: #22d3ee; }
      .menu-btn {
         background: #0b1220;
         border-color: #334155;
         color: #cbd5e1;
      }
      .menu-btn:hover {
         background: #111827;
         border-color: #475569;
         color: #67e8f9;
      }
      .menu-pop {
         background: #0b1220;
         border-color: #334155;
      }
      .menu-item {
         color: #e2e8f0;
      }
      .menu-item:hover {
         background: #111827;
         color: #f8fafc;
      }
      .menu-item.delete:hover {
         background: rgba(220, 38, 38, 0.18);
         color: #fda4af;
      }
      &:hover { border-color: #22d3ee; box-shadow: 0 10px 20px rgba(34, 211, 238, 0.12); }
   }
`;

export const LandingEmpty = styled.div`
   text-align: center;
   padding: 4rem 2rem;
   color: #64748b;
   background-color: #fff;
   border-radius: 1rem;
   box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
   animation: ${fadeIn} 0.5s ease-out;
   display: flex;
   align-items: center;
   justify-content: center;
   flex-direction: column;
   gap: 10px;

   h3 {
      margin: 0;
      color: #0f172a;
      font-size: 1.5rem;
   }

   p {
      margin: 0;
      font-size: 1rem;
      max-width: 450px;
      line-height: 1.6;
   }

   [data-theme="dark"] & {
      background: #111827;
      color: #9ca3af;
      h3 { color: #e2e8f0; }
   }
`;

export const CardActionModalOverlay = styled.div<{ isVisible: boolean }>`
   position: fixed;
   top: 0;
   left: 0;
   right: 0;
   bottom: 0;
   background-color: rgba(0, 0, 0, 0.6);
   display: flex;
   align-items: center;
   justify-content: center;
   z-index: 120;
   opacity: ${p => p.isVisible ? 1 : 0};
   visibility: ${p => p.isVisible ? 'visible' : 'hidden'};
   transition: opacity 0.25s ease, visibility 0.25s ease;
   backdrop-filter: blur(3px);
`;

export const CardActionModalContainer = styled.div`
   width: 100%;
   max-width: 500px;
   margin: 0 1rem;
   border-radius: 1rem;
   border: 1px solid #e2e8f0;
   background: #fff;
   box-shadow: 0 18px 40px rgba(15, 23, 42, 0.24);
   padding: 1.5rem;
   animation: ${fadeIn} 0.25s ease;

   [data-theme="dark"] & {
      background: #0f172a;
      border-color: #334155;
      box-shadow: 0 18px 40px rgba(2, 6, 23, 0.45);
   }
`;

export const CardActionModalHeader = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   margin-bottom: 1.25rem;
`;

export const CardActionModalTitle = styled.h3`
   margin: 0;
   font-size: 1.2rem;
   font-weight: 700;
   color: #0f172a;

   [data-theme="dark"] & {
      color: #e2e8f0;
   }
`;

export const CardActionModalCloseBtn = styled.button`
   width: 34px;
   height: 34px;
   border-radius: 0.5rem;
   border: 1px solid #e2e8f0;
   background: #fff;
   color: #64748b;
   cursor: pointer;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   transition: all 0.2s ease;

   &:hover {
      background: #f8fafc;
      border-color: #cbd5e1;
      color: #1e293b;
   }

   [data-theme="dark"] & {
      border-color: #334155;
      background: #0b1220;
      color: #cbd5e1;
   }

   [data-theme="dark"] &:hover {
      background: #111827;
      border-color: #475569;
      color: #f8fafc;
   }
`;

export const CardActionModalFormGroup = styled.div`
   margin-bottom: 1.25rem;

   label {
      display: block;
      font-size: 0.92rem;
      font-weight: 600;
      margin-bottom: 0.45rem;
      color: #334155;
   }

   input {
      width: 100%;
      padding: 0.85rem 1rem;
      border-radius: 0.5rem;
      border: 2px solid #e2e8f0;
      background: #fff;
      color: #0f172a;
      font-size: 1rem;
      transition: all 0.25s ease;
   }

   input:focus {
      outline: none;
      border-color: #4361ee;
      box-shadow: 0 0 0 3px rgba(67, 97, 238, 0.12);
   }

   input::placeholder {
      color: #94a3b8;
   }

   [data-theme="dark"] & label {
      color: #cbd5e1;
   }

   [data-theme="dark"] & input {
      background: #111827;
      border-color: #334155;
      color: #e2e8f0;
   }
`;

export const CardActionModalActions = styled.div`
   display: flex;
   justify-content: flex-end;
   gap: 0.75rem;
   margin-top: 0.75rem;
`;

export const CardActionModalButton = styled.button<{ $primary?: boolean; $danger?: boolean }>`
   border: none;
   border-radius: 0.5rem;
   padding: 0.72rem 1.4rem;
   font-size: 0.875rem;
   font-weight: 600;
   cursor: pointer;
   transition: all 0.2s ease;
   background: ${p =>
      p.$primary
         ? 'linear-gradient(to right, #4361ee, #4cc9f0)'
         : p.$danger
            ? '#ef4444'
            : '#f1f5f9'};
   color: ${p => (p.$primary || p.$danger ? '#fff' : '#334155')};

   &:hover {
      transform: translateY(-1px);
      box-shadow: 0 8px 18px rgba(15, 23, 42, 0.12);
   }

   &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
   }

   [data-theme="dark"] & {
      background: ${p =>
         p.$primary
            ? 'linear-gradient(to right, #4361ee, #4cc9f0)'
            : p.$danger
               ? '#dc2626'
               : '#1f2937'};
      color: ${p => (p.$primary || p.$danger ? '#fff' : '#e2e8f0')};
   }
`;